import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Tag, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get correct image URL for both local and R2 storage
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  // If URL is absolute (starts with http), use as-is (Cloudflare R2)
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // Otherwise, prepend backend URL (local storage)
  return `${BACKEND_URL}${imageUrl}`;
};

export const GalleryPublic = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, [selectedCategory]);

  const fetchEvents = async () => {
    try {
      const url = selectedCategory === 'all'
        ? `${BACKEND_URL}/api/public/gallery-events`
        : `${BACKEND_URL}/api/public/gallery-events?category=${selectedCategory}`;
      
      const response = await axios.get(url);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/gallery/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'training': 'Training Sessions',
      'events': 'Events',
      'workshops': 'Workshops',
      'conferences': 'Conferences',
      'certification': 'Certifications',
      'general': 'General'
    };
    return labels[category] || category;
  };

  const openEvent = (event) => {
    setSelectedEvent(event);
    setCurrentImageIndex(0);
  };

  const closeEvent = () => {
    setSelectedEvent(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedEvent && selectedEvent.images) {
      setCurrentImageIndex((prev) => 
        prev < selectedEvent.images.length - 1 ? prev + 1 : 0
      );
    }
  };

  const prevImage = () => {
    if (selectedEvent && selectedEvent.images) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : selectedEvent.images.length - 1
      );
    }
  };

  // Get first valid image from event
  const getEventThumbnail = (event) => {
    if (event.images && Array.isArray(event.images) && event.images.length > 0) {
      // Filter out null entries
      const validImages = event.images.filter(img => img && img.image_url);
      return validImages.length > 0 ? validImages[0].image_url : null;
    }
    return null;
  };

  const getImageCount = (event) => {
    if (event.images && Array.isArray(event.images)) {
      return event.images.filter(img => img && img.image_url).length;
    }
    return 0;
  };

  // Get SEO data based on selected category
  const getSEOData = () => {
    const categoryLabels = {
      'training': 'Training Sessions',
      'events': 'Events',
      'workshops': 'Workshops',
      'conferences': 'Conferences',
      'certification': 'Certifications',
      'general': 'General'
    };

    const categoryLabel = selectedCategory !== 'all'
      ? categoryLabels[selectedCategory] || selectedCategory
      : 'Gallery';

    const title = `${categoryLabel} Gallery - ISDE Academy`;
    const description = selectedCategory !== 'all'
      ? `View photos from our ${categoryLabel.toLowerCase()} at ISDE Academy. Professional training documentation and event highlights.`
      : 'Browse our complete gallery of training sessions, events, workshops, and conferences at ISDE Academy. Professional training documentation since 2015.';

    const keywords = selectedCategory !== 'all'
      ? `${categoryLabel}, ISDE Academy, training photos, ${selectedCategory} gallery, professional development`
      : 'ISDE Academy gallery, training photos, event gallery, workshops, professional development, training documentation';

    // Get first event's image for og:image if available
    const ogImage = events.length > 0 && events[0].images?.length > 0
      ? getImageUrl(events[0].images[0].image_url)
      : null;

    return { title, description, keywords, ogImage };
  };

  const seoData = getSEOData();

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        ogImage={seoData.ogImage}
        type="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          "name": seoData.title,
          "description": seoData.description,
          "url": window.location.href,
          "numberOfItems": events.length,
          "provider": {
            "@type": "Organization",
            "name": "ISDE Academy",
            "url": "https://isde.academy"
          }
        }}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Gallery</h1>
            <p className="text-xl text-blue-100">
              Moments from our training sessions, events, and workshops
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setSelectedCategory('all')}
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' ? 'bg-blue-600' : ''}
            >
              All ({events.length})
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.category}
                onClick={() => setSelectedCategory(cat.category)}
                variant={selectedCategory === cat.category ? 'default' : 'outline'}
                className={selectedCategory === cat.category ? 'bg-blue-600' : ''}
              >
                {getCategoryLabel(cat.category)} ({cat.count})
              </Button>
            ))}
          </div>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading gallery...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No events available in this category</p>
          </div>
        ) : (
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {events.map((event, index) => {
              const thumbnail = getEventThumbnail(event);
              const imageCount = getImageCount(event);
              
              return (
                <motion.div
                  key={event.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow cursor-pointer bg-white"
                  onClick={() => openEvent(event)}
                >
                  <div className="aspect-square overflow-hidden bg-gray-100">
                    {thumbnail ? (
                      <img
                        src={getImageUrl(thumbnail)}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-16 w-16 text-gray-300" />
                      </div>
                    )}
                  </div>
                  
                  {imageCount > 1 && (
                    <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                      <ImageIcon className="h-3 w-3" />
                      {imageCount}
                    </div>
                  )}
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-semibold text-lg mb-1 line-clamp-2">{event.title}</h3>
                      <div className="flex items-center gap-3 text-sm">
                        {event.category && (
                          <span className="flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            {getCategoryLabel(event.category)}
                          </span>
                        )}
                        {event.event_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(event.event_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* Event Lightbox Modal with Image Carousel */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={closeEvent}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20 z-10"
              onClick={closeEvent}
            >
              <X className="h-6 w-6" />
            </Button>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-6xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedEvent.images && selectedEvent.images.length > 0 ? (
                <>
                  {/* Image Display */}
                  <div className="relative mb-4">
                    <img
                      src={getImageUrl(selectedEvent.images[currentImageIndex]?.image_url)}
                      alt={selectedEvent.images[currentImageIndex]?.caption || selectedEvent.title}
                      className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                    />
                    
                    {/* Navigation Arrows */}
                    {selectedEvent.images.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); prevImage(); }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition"
                        >
                          <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); nextImage(); }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition"
                        >
                          <ChevronRight className="h-6 w-6" />
                        </button>
                        
                        {/* Image Counter */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                          {currentImageIndex + 1} / {selectedEvent.images.length}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Event Info */}
                  <div className="bg-white p-6 rounded-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedEvent.title}</h2>
                    {selectedEvent.description && (
                      <p className="text-gray-700 mb-4">{selectedEvent.description}</p>
                    )}
                    {selectedEvent.images[currentImageIndex]?.caption && (
                      <p className="text-gray-600 italic mb-4">
                        "{selectedEvent.images[currentImageIndex].caption}"
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {selectedEvent.category && (
                        <span className="flex items-center gap-1">
                          <Tag className="h-4 w-4" />
                          {getCategoryLabel(selectedEvent.category)}
                        </span>
                      )}
                      {selectedEvent.event_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(selectedEvent.event_date).toLocaleDateString()}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <ImageIcon className="h-4 w-4" />
                        {selectedEvent.images.length} {selectedEvent.images.length === 1 ? 'image' : 'images'}
                      </span>
                    </div>
                  </div>

                  {/* Thumbnail Strip */}
                  {selectedEvent.images.length > 1 && (
                    <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
                      {selectedEvent.images.map((img, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                          className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition ${
                            idx === currentImageIndex ? 'border-blue-500' : 'border-transparent'
                          }`}
                        >
                          <img
                            src={getImageUrl(img.image_url)}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white p-12 rounded-lg text-center">
                  <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedEvent.title}</h2>
                  <p className="text-gray-600">No images available for this event</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
